import { uniq } from 'lodash-es';

const matchLeadingPadding = /^\d0+/u;

/**
 * The product group prefix for a design id
 */
export enum LEGOProductGroup {
  ElementDesign = 10,
  PackagingDesign = 20,
  PrintedMattersDesign = 30,
  RawMaterialsDesign = 40,
  ProductDesign = 50,
  VirtualDesign = 60,
  PrepackDesign = 70,
  ConceptDesign = 90,
}

/**
 * The type group for a design id
 *
 * @remarks The order is important. The first is most significant then goes to least significant.
 */
const selectionTypeGroups = [
  { prefix: '30', regex: /^30\d{6}$/u, name: 'Building Instruction' },
  { prefix: '70', regex: /^70\d{6}$/u, name: 'Prepack Technical' },
  { prefix: '20', regex: /^20\d{6}$/u, name: 'Packaging Card Board Plastic' },
  { prefix: '50', regex: /^50\d{6}$/u, name: 'Finished Goods' },
  { prefix: '5', regex: /^5[1-9]{2}\d{5}$/u, name: 'LE Finished Goods' },
  { prefix: '1000', regex: /^1000\d{4}$/u, name: 'Element' },
  { prefix: '10', regex: /^10\d{6}$/u, name: 'Element or Decoration Sticker' },
];

/**
 * Converts a Design ID into a Communication ID
 *
 * @example
 * '10003001' -> '3001'
 * '10057028' -> '57028'
 * '10100039' -> '100039'
 *
 * @param designId An 8 digit Design ID
 * @returns The Communication ID representation of the given Design ID
 */
export const convertDesignIdToCommunicationId = (designId: string): string =>
  designId.replace(matchLeadingPadding, '');

/**
 * Converts a Communication ID into an Design ID by adding the leading product group number
 *
 * @example
 * ('3001', ElementDesign)   -> '10003001'
 * ('57028', ElementDesign)  -> '10057028'
 * ('100039', ElementDesign) -> '10100039'
 * ('1234', ConceptDesign)   -> '90001234'
 * ('56789', ConceptDesign)  -> '90056789'
 * ('xxxxxx', ConceptDesign) -> '90xxxxxx'
 *
 * @param communicationId A 4,5,6 digit Communication ID
 * @param productGroup The product group the design belongs to
 * @returns The Element Design ID representation of the given Communication ID
 */
export const convertCommunicationIdToDesignId = (
  communicationId: string,
  productGroup: LEGOProductGroup
): string => `${productGroup}${communicationId.padStart(6, '0')}`;

/**
 * Converts a Communication ID into an Element Design ID by adding the leading 1000 or 100
 *
 * @example
 * '3001'   -> '10003001'
 * '57028'  -> '10057028'
 * '100039' -> '10100039'
 *
 * @param communicationId A 4,5,6 digit Communication ID
 * @returns The Element Design ID representation of the given Communication ID
 */
export const convertCommunicationIdToElementDesignId = (communicationId: string): string =>
  convertCommunicationIdToDesignId(communicationId, LEGOProductGroup.ElementDesign);

/**
 * Check whether an id is in a valid Design ID format
 * A valid id is an 8 digit long string
 *
 * @example
 * '3001'     -> false
 * '10057028' -> true
 * 'alphanum' -> false
 * '12345678' -> true // beware of this
 *
 * @param id A string representing a design id
 * @returns `true` if the id is a Design ID, else `false`
 */
export const isDesignId = (id: string): boolean => !Number.isNaN(id) && id.length === 8;

/**
 * Converts a given id to a design ID given the length of the id and the groups.
 *
 * @param id A string representing a design id
 * @returns An array of the possible design ids converted into 8 digit numbers.
 */
export const convertIdToPossibleDesignIds = (id: string): string[] => {
  const possibleDesignIds = selectionTypeGroups
    .map(
      (selectionTypeGroup) =>
        selectionTypeGroup.prefix + id.padStart(8 - selectionTypeGroup.prefix.length, '0')
    )
    .filter((possibleDesignId) => possibleDesignId.length === 8);

  return uniq(possibleDesignIds);
};

export const getLEGOSelectionTypeGroup = (designId: string): string =>
  selectionTypeGroups.find((selectionTypeGroup) => selectionTypeGroup.regex.test(designId))?.name ??
  'Unknown prefix';

/**
 * Converts an Equipment Number into a Communication ID by removing the Running Number suffix
 *
 * @example
 * '3001155'   -> '3001'
 * '57028002'  -> '57028'
 * '100039002' -> '100039'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @returns The Communication ID part of the Equipment Number
 */
export const convertEquipmentNumberToCommunicationId = (equipmentNumber: string): string =>
  equipmentNumber.slice(0, -3);

/**
 * Converts an Equipment Number into a Design ID by removing the Running Number suffix and adding the leading 1000 or 100
 *
 * @example
 * ('3001155', ElementDesign)   -> '10003001'
 * ('57028002', ElementDesign)  -> '10057028'
 * ('100039002', ElementDesign) -> '10100039'
 * ('1234001', ConceptDesign)   -> '90001234'
 * ('5678901', ConceptDesign)   -> '90056789'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @param productGroup The product group the design belongs to
 * @returns The Design ID for the Equipment Number
 */
export const convertEquipmentNumberToDesignId = (
  equipmentNumber: string,
  productGroup: LEGOProductGroup
): string =>
  convertCommunicationIdToDesignId(
    convertEquipmentNumberToCommunicationId(equipmentNumber),
    productGroup
  );

/**
 * Converts an Equipment Number into a Design ID by removing the Running Number suffix and adding the leading 1000, 100 or 10
 *
 * @example
 * '3001155'   -> '10003001'
 * '57028002'  -> '10057028'
 * '100039002' -> '10100039'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @returns The Design ID for the Equipment Number
 */
export const convertEquipmentNumberToElementDesignId = (equipmentNumber: string): string =>
  convertCommunicationIdToElementDesignId(convertEquipmentNumberToCommunicationId(equipmentNumber));

/**
 * Converts an equipmentNumber into an objectKey by adding a number of leading zeroes
 *
 * @example
 * '3002080'   -> '000000000003002080'
 * '11153011'  -> '000000000011153011'
 * 'xxxxxxxxx' -> '000000000xxxxxxxxx'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @returns The Object Key representation of the given equipment number
 */
export const convertEquipmentNumberToObjectKey = (equipmentNumber: string): string =>
  equipmentNumber.padStart(18, '0');

/**
 * Converts an objectKey into a mould card objectKey by adding a number of leading zeroes
 *
 * @example
 * '000000000003002080' -> '0000000000000000003002080'
 *
 * @param objectKey Object key in the format xxxxxxxxxxxxxxxxxx
 * @returns The Mould card Object Key representation of the given Object key
 */
export const convertObjectKeyToMouldCardObjectKey = (objectKey: string): string =>
  objectKey.padStart(25, '0');

/**
 * Converts a changeNumber into an objectKey by adding a number of leading zeroes
 *
 * @example
 * '3002080' -> '000003002080'
 *
 * @param changeNumber change Number in format xxxxxx or xxxxxxx
 * @returns The Object Key representation of the given change number
 */
export const convertChangeNumberToObjectKey = (changeNumber: string): string =>
  changeNumber.padStart(12, '0');

/**
 * Converts an Equipment Number into an Equipment Running Number
 *
 * @example
 * '3011106'   -> '106'
 * '57028002'  -> '002'
 * 'xxxxxx003' -> '003'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @returns The last 3 digits in the Equipment Number aka Equipment Running Number
 */
export const convertEquipmentNumberToEquipmentRunningNumber = (equipmentNumber: string): string =>
  equipmentNumber.slice(-3);

/**
 * Converts a taskListGroup from a TaskListHeader to designId
 *
 * @example
 * 'TS026047' -> '10026047'
 * 'TS003001' -> '10003001'
 *
 * 'TSA26047' -> '10026047'
 * 'TSA03001' -> '10003001'
 *
 * @remarks It have been decided that these will not support the new 6 digit communication id
 *
 * @param taskListGroup A TS0/TSA + 5 digits
 * @returns The Element Design ID representation of the given taskListGroup
 */
export const convertTaskListGroupToElementDesignId = (taskListGroup: string): string =>
  `${LEGOProductGroup.ElementDesign}0${taskListGroup.slice(3)}`;

/**
 * Converts a Design ID into possible taskListGroups
 *
 * @example
 * '10003001' -> ['TS003001', 'TSA03001']
 * '10026047' -> ['TS026047', 'TSA26047']
 * '10102745' -> ['TS102745']
 *
 * @remarks 6 digit communication ids are considered mounted elements and will not have a TSA
 *
 * @param designId An 8 digit Design ID
 * @returns Possible taskListGroups for the Design ID
 */
export const convertDesignIdToTaskListGroups = (designId: string): string[] => {
  const communicationId = convertDesignIdToCommunicationId(designId);

  if (communicationId.length === 6) {
    return [`TS${communicationId}`];
  }

  const zeroPaddedCommunicationId = communicationId.padStart(5, '0');

  return [`TS0${zeroPaddedCommunicationId}`, `TSA${zeroPaddedCommunicationId}`];
};

/**
 * Converts an equipmentNumber into a equipmentNumber used by visitation by adding leading 100 or 1000
 *
 * @remarks
 * This is not a real number/id but visitation has incorrectly formatted it like that
 *
 * @example
 * '3001001'    -> '10003001001'
 * '29387001'   -> '10029387001'
 * 'xxxxxxxxx'  -> '10xxxxxxxxx'
 *
 * @param equipmentNumber A 7,8,9 digit Equipment Number
 * @returns The visitation equipment number representation of the given equipment number
 */
export const convertEquipmentNumberToVisitationEquipmentNumber = (
  equipmentNumber: string
): string => `${LEGOProductGroup.ElementDesign}${equipmentNumber.padStart(9, '0')}`;
